import { Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { featureTitleEnum } from 'constants/ui.constants';
import { ButtonComponent } from 'components/ButtonComponent';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getBusinessPlans, getError, isLoading, setError } from 'state/reducers/plans-business-model.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getBusinessModelsPlans, saveBusinessModelsPlans } from 'services/plans-business-model.service';
import { BusinessPlanItem } from 'sections/BusinessPlans/BusinessPlanItem';
import { CoinsCouples } from 'sections/BusinessPlans/CoinsCouples';
import { planInputType } from 'constants/business-plan.constants';
import { PopupComponent } from 'components/PopupComponent';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { WarningDialog } from 'components/WarningDialog';
import { getPagesByFeature } from 'services/content.service';
import { getPages } from 'state/reducers/content.reducers';
import _ from 'lodash';
import { preparePlanData } from 'sections/BusinessPlans/helper';

export const BusinessModelApp = () => {
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const loading = useAppSelector(isLoading);
	const pages = useAppSelector(getPages);
	const businessPlans = useAppSelector(getBusinessPlans);
	const [plansTableBodyRowsData, setPlansTableBodyRowsData] = useState<any>(null);
	const [businessPlansItems, setBusinessPlansItems] = useState<any>(null);
	const [planToEdit, setPlanToEdit] = useState<any>(null);
	const businessModelError = useAppSelector(getError);
	const planEditPermission = hasPermission([
		permissionEnums.BUSINESS_MODEL_PLAN_EDITOR,
		permissionEnums.BUSINESS_MODEL_CONTENT,
	]);

	const namesMapping = {};
	const openErrorDialog = useMemo(() => {
		return businessModelError?.exist;
	}, [businessModelError]);

	useEffect(() => {
		if (planEditPermission) {
			dispatch(getBusinessModelsPlans());
			dispatch(getPagesByFeature('plans', 'plans'));
		}
	}, [planEditPermission]);

	useEffect(() => {
		if (businessPlans?.length > 0) {
			const data = preparePlanData(businessPlans, t);
			setPlansTableBodyRowsData(data);
		}
	}, [businessPlans]);

	useEffect(() => {
		if (plansTableBodyRowsData?.length > 0) {
			const pagesObj = _.keyBy(pages, 'page');
			const plans = businessPlans.map((plan: any, index: number) => {
				const planName = _.get(
					pagesObj,
					`[${_.toLower(plan?.name)} plan].sections[0].fieldGroups[0].fieldGroupItems[0].he`
				);
				const tableHeaders = !planEditPermission
					? ['', <img key="" src="" />]
					: [
							'',
							<img
								key="editIcon"
								src={'icons/pencil.svg'}
								style={{ cursor: 'pointer' }}
								onClick={() => {
									setPlanToEdit(_.isNil(planToEdit) ? plan : null);
								}}
							/>,
					  ];
				return (
					<BusinessPlanItem
						key={index}
						planName={planName}
						item={plan}
						tableHeaderItems={tableHeaders}
						data={plansTableBodyRowsData ? plansTableBodyRowsData[index] : []}
						planToEdit={planToEdit}
						handleInputChange={handlePlanToEditChanges}
					/>
				);
			});
			setBusinessPlansItems([...plans]);
		}
	}, [plansTableBodyRowsData, planToEdit]);

	const handlePlanToEditChanges = (event: any, planPointer: string) => {
		const copyPlanToEdit = { ...planToEdit };
		copyPlanToEdit[planPointer] = parseFloat(event) || event;
		setPlanToEdit(copyPlanToEdit);
	};

	const handleSavePlan = () => {
		dispatch(
			saveBusinessModelsPlans(
				planToEdit,
				businessPlans.findIndex((plan: any) => plan?.id === planToEdit?.id),
				t('page.businessPlans.serverError')
			)
		);
		setPlanToEdit(null);
	};

	return (
		<Fragment>
			<WarningDialog
				open={openErrorDialog}
				subTitle={businessModelError?.text}
				handleCancel={() => {
					dispatch(setError({ exist: false }));
				}}
			/>
			<Page>
				<>
					<Stack spacing={6}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<Typography variant="pageTitle">{featureTitleEnum.PLANS_BUSINESS_MODEL}</Typography>
							{planEditPermission && (
								<ButtonComponent
									disabled={loading || !planToEdit}
									handleClick={handleSavePlan}
									text={t('button.save_changes')}
									icon={
										<img
											src={'/icons/save.svg'}
											style={{
												marginLeft: '10px',
												filter: planToEdit
													? 'brightness(5) grayscale(100%) contrast(2)'
													: 'brightness(0.1)',
												opacity: planToEdit ? 1 : 0.3,

												transform: 'scale(0.8)',
											}}
										/>
									}
									styleObj={{ flexDirection: 'row-reverse' }}
								/>
							)}
						</Box>
						<Box
							sx={{
								marginBottom: '24px',
								display: 'grid',
								gridGap: '32px',
								gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
								[theme.breakpoints.down('md')]: {
									gridTemplateColumns: '100%',
								},
							}}
						>
							{businessPlansItems &&
								businessPlansItems.length > 0 &&
								businessPlansItems.map((item: any) => {
									return item;
								})}
						</Box>
						<CoinsCouples />
					</Stack>
				</>
			</Page>
		</Fragment>
	);
};
